import React, { Fragment, useEffect, useMemo, useState } from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import DimpleDivider from '@tds/core-dimple-divider';
import { Error } from '../design/elements';
import FormField from './formField';
import {
  SeatDetailsButtonWrapper,
  BoxWrapper,
  GroupTitle,
  GroupWrapper
} from './css';
import SeatDetailsTable from './seatDetailsTable';
import request from '../utils/request';
import submitPayload from './formSubmitLogic';
import {
  contentFulFields,
  fieldBusinessConnectEdition,
  fieldCoperToFibreMigration,
  fieldNoteOfficeSeat,
  fieldNoteStarterSeat,
  fieldWillTheTollFreeBeBortedIn,
  fieldWillTheLocalPhoneNumberBePortedIn,
  fieldProvider,
  orderDetailsForm,
  fieldC2fAutomatedActivation,
  fieldOfficeSeatFirstName,
  fieldOfficeSeatLastName,
  fieldOfficeSeatEmail
} from '../utils/constant';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import stringInterpolation from '../utils/stringInterpolation';
import ValidateLoader from '../UI/validateLoader';
import { getPathVariables } from '../misc';

const SeatDetailsForm = ({
  data,
  orderData,
  updateStep,
  step,
  setOrderData,
  onSaveAndExit,
  isOrderFulfillmentTeam,
  prevStep
}) => {
  const history = useHistory();
  const {
    location: { pathname }
  } = history;
  const [locale, categoryName] = getPathVariables(pathname);
  const { starterSeatDetails, officeSeatDetails } = contentFulFields;
  const applyToX = 'applyToTheNextXSeatsOnThisOrder';
  const deviceType = 'deviceType';
  const localizedStrings = useLocalizedStringsContext();
  const {
    seatDetails: {
      errorMessage,
      errorTotalMessage,
      fieldRequiredMessage,
      npaLabel,
      nxxLabel
    },
    formButtons: {
      continueButtonLabel,
      nextSeatButtonLabel,
      saveContinueLaterButtonLabel,
      backButtonLabel
    },
    contentfulValues: {
      yes,
      deviceBYOD,
      editionMapping: { voice }
    },
    general: { loading }
  } = localizedStrings;
  const { name, key, formFields } = data;
  const isOfficeSeat = key === officeSeatDetails;
  const seatCount = isOfficeSeat
    ? parseInt(orderData.officeSeats, 10)
    : parseInt(orderData.starterSeat, 10);
  const totalSeatCount =
    parseInt(orderData.officeSeats, 10) + parseInt(orderData.starterSeat, 10);
  const isc2fMigrationFlow =
    _.get(orderData, fieldCoperToFibreMigration, '') === yes;
  const isc2fAutomatedActivation =
    _.get(orderData, fieldC2fAutomatedActivation, '') === yes;

  const formObjectKeys = useMemo(() => {
    return formFields.reduce(
      (acc, { fields: { name: formName, value } }) => {
        acc[formName] = value;
        return acc;
      },
      {
        npa: 0,
        nxx: 0,
        number: 0,
        devicePaymentMethod: ''
      }
    );
  }, [formFields]);

  const [fieldErrors, setFieldErrors] = useState({});
  const [rateCenterData, setRateCenterData] = useState();
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [forms, setForms] = useState([]);
  const [currentForm, setCurrentForm] = useState({
    ...formObjectKeys,
    creationDate: new Date()
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const searchString = paramStep => {
    return orderData && orderData.id
      ? `?orderId=${orderData.id}&step=${paramStep}`
      : '';
  };
  const redirect = newStep => {
    updateStep(newStep);
    history.push({
      pathname: `/${locale}/${categoryName}/${orderDetailsForm}`,
      search: searchString(newStep)
    });
  };

  const submitOrder = async updateFormsData => {
    const payload = { ...orderData, [key]: updateFormsData };
    if (isOfficeSeat) {
      let formsText = forms.reduce((acc, item) => {
        const text = acc ? `${acc}\n` : acc;
        return item[fieldNoteOfficeSeat]
          ? `${text}${item[fieldNoteOfficeSeat]}`
          : acc;
      }, '');
      formsText = formsText ? `${formsText}\n` : formsText;
      formsText = formsText
        ? `${formsText}${currentForm[fieldNoteOfficeSeat]}`
        : '';
      payload[fieldNoteOfficeSeat] = formsText;
    } else {
      payload[fieldNoteStarterSeat] = currentForm[fieldNoteStarterSeat];
    }
    const response = await submitPayload(payload, step + 1);
    setOrderData(response.data);
    redirect(step + 1);
  };

  useEffect(() => {
    if (Object.keys(orderData).length !== 0) {
      if (seatCount === 0 || Number.isNaN(seatCount)) {
        submitOrder([]);
      }
    }
  }, [orderData]);

  useEffect(() => {
    request
      .get(`/rateCenter?country=CA&sortBy=RCLONG&sortOrder=ASC`)
      .then(response => {
        if (response && Array.isArray(response.data)) {
          setRateCenterData(response.data);
        }
      });
  }, [data]);

  const rateCenterObjectMap = useMemo(
    () =>
      rateCenterData &&
      rateCenterData.reduce((acc, rateCenter) => {
        const { npa, nxx, rclong, newDid, province } = rateCenter;
        if (acc[province]) {
          if (acc[province][rclong]) {
            if (acc[province][rclong][npa]) {
              if (acc[province][rclong][npa][nxx]) {
                acc[province][rclong][npa][nxx] = true;
              } else {
                acc[province][rclong][npa][nxx] = {};
              }
            } else {
              acc[province][rclong][npa] = {
                [nxx]: true
              };
            }
            if (acc[province][rclong].newDidCount) {
              if (newDid) acc[province][rclong].newDidCount += 1;
            } else {
              acc[province][rclong].newDidCount = newDid ? 1 : 0;
            }
          } else {
            acc[province][rclong] = {
              [npa]: {
                [nxx]: true
              },
              name: rclong,
              newDidCount: newDid ? 1 : 0
            };
          }
        } else {
          acc[province] = {
            [rclong]: {
              [npa]: {
                [nxx]: true
              },
              name: rclong
            }
          };
        }
        return acc;
      }, {}),
    [rateCenterData]
  );

  const portingNumbers = useMemo(
    () =>
      rateCenterData &&
      rateCenterData.reduce((acc, rateCenter) => {
        const { npa, nxx, province } = rateCenter;
        if (acc[province]) {
          if (acc[province][npa]) {
            acc[province][npa].push(nxx);
          } else {
            acc[province][npa] = [];
            acc[province][npa].push(nxx);
          }
        } else {
          acc[province] = {};
        }
        return acc;
      }, {}),
    [rateCenterData]
  );

  const devicesMap = useMemo(() => {
    if (orderData && orderData.devices) {
      return orderData.devices.reduce(
        (acc, { name: deviceName, payForDevice }) => {
          acc[deviceName] = payForDevice;
          return acc;
        },
        {}
      );
    }
    return {};
  }, [orderData]);

  const getDeviceQuantity = deviceName => {
    const getTotal = arrayData =>
      arrayData.reduce((acc, item) => {
        return item[deviceType] === deviceName ? acc + 1 : acc;
      }, 0);
    let result = 0;

    if (orderData[starterSeatDetails]) {
      result += getTotal(orderData[starterSeatDetails]);
    }
    if (forms.length) {
      result += getTotal(forms);
    } else if (!isFormTouched && orderData[officeSeatDetails]) {
      result += getTotal(orderData[officeSeatDetails]);
    }
    if (
      currentForm &&
      typeof currentForm[deviceType] !== 'undefined' &&
      currentForm[deviceType] !== deviceName
    ) {
      if (step === 1 && orderData[starterSeatDetails]) {
        const isFound = orderData[starterSeatDetails].find(
          item => item.deviceType === deviceName
        );
        if (typeof isFound !== 'undefined') result -= 1;
      }
    }

    return result;
  };

  const getByodInitQuantity = () => {
    const count =
      (orderData &&
        orderData.devices &&
        orderData.devices.reduce(
          (acc, device) => acc + (device.quantity || 0),
          0
        )) ||
      0;
    return totalSeatCount - count;
  };

  const getDeviceInitQuantity = deviceName =>
    deviceName === deviceBYOD
      ? getByodInitQuantity()
      : orderData.devices &&
        orderData.devices.reduce(
          (acc, item) => (item.name === deviceName ? acc + item.quantity : acc),
          0
        );

  const devices = () => {
    let result = [];
    let totalDeviceCount = 0;
    if (orderData && orderData.devices) {
      const uniqueNames = orderData.devices.reduce(
        (acc, item) => (acc.includes(item.name) ? acc : [...acc, item.name]),
        []
      );

      result = uniqueNames.map(item => {
        const count = getDeviceInitQuantity(item) - getDeviceQuantity(item);
        totalDeviceCount += getDeviceInitQuantity(item);
        return {
          fields: {
            value: item,
            label: `${item} (${count})`,
            disabled: count < 1
          }
        };
      });
    }
    if (totalDeviceCount !== totalSeatCount) {
      const byodCount =
        getDeviceInitQuantity(deviceBYOD) - getDeviceQuantity(deviceBYOD);
      result.push({
        fields: {
          value: deviceBYOD,
          label: `${deviceBYOD} (${byodCount})`,
          disabled: byodCount < 1
        }
      });
    }
    return result;
  };

  const formFieldMap = useMemo(
    () =>
      formFields.reduce((acc, { fields }) => {
        const { name: fieldName } = fields;
        const updatedFields = { ...fields };
        acc[fieldName] = { ...updatedFields };
        return acc;
      }, {}),
    [formFields]
  );

  const getRateCenterOptions = province => {
    return (
      province &&
      rateCenterObjectMap[province] &&
      Object.keys(rateCenterObjectMap[province]).map(
        rateCenter =>
          rateCenterObjectMap[province][rateCenter].newDidCount > 0 && {
            fields: {
              label: rateCenterObjectMap[province][rateCenter].name,
              value: rateCenter
            }
          }
      )
    );
  };

  const getNPAOptions = province => {
    if (province) {
      const npaMap = portingNumbers[province];
      return Object.keys(npaMap).sort();
    }
    return [];
  };

  const getNXXOptions = province => {
    if (province && currentForm.npa) {
      return portingNumbers[province][currentForm.npa].sort();
    }
    return [];
  };

  useEffect(() => {
    if (
      orderData &&
      orderData.id &&
      orderData[key] &&
      rateCenterData &&
      rateCenterData.length
    ) {
      const dbFormData = orderData[key];
      const seats = dbFormData.length;
      if (seats) {
        const newIndex = seats - 1;
        const currentFormData = dbFormData[newIndex];
        setCurrentForm({ ...currentFormData });
        setCurrentIndex(newIndex);
        dbFormData.splice(newIndex, 1);
        setForms(dbFormData);
      }
    }
  }, [rateCenterData]);

  const isRelatedFieldExisted = field => {
    const { name: fieldName, appearsAfterField, appearsAfterValue } = field;

    if (
      orderData[fieldBusinessConnectEdition] === voice &&
      [fieldWillTheTollFreeBeBortedIn].includes(fieldName)
    ) {
      return false;
    }

    if (!appearsAfterField) return true;

    let isVisible = true;
    for (let i = 0; i < appearsAfterField.length; i += 1) {
      const {
        fields: { name: relatedFieldName }
      } = appearsAfterField[i];
      if (typeof currentForm !== 'undefined') {
        const relatedFieldValue = currentForm[relatedFieldName];
        isVisible =
          isVisible &&
          appearsAfterValue.find(
            item => relatedFieldValue === item.fields.value
          );
      }
    }
    if (
      isc2fAutomatedActivation &&
      [
        fieldOfficeSeatFirstName,
        fieldOfficeSeatLastName,
        fieldOfficeSeatEmail
      ].includes(fieldName)
    )
      isVisible = true;

    return isVisible;
  };

  const handleFormField = (fieldName, value) => {
    setFieldErrors({});
    let updateValue = value;

    if (fieldName === applyToX && seatCount - forms.length <= +value) {
      setFieldErrors({
        applyToTheNextXSeatsOnThisOrder: stringInterpolation(
          errorMessage,
          seatCount
        )
      });
      updateValue = seatCount - forms.length - 1;
    }
    const updatedForm = { ...currentForm };
    if (isc2fMigrationFlow) {
      updatedForm.willTheLocalPhoneNumberBePortedIn = 'Yes';
      updatedForm.provider = 'TELUS';
      updatedForm[fieldWillTheTollFreeBeBortedIn] = 'No';
    }

    if (fieldName === deviceType) {
      updatedForm.devicePaymentMethod = devicesMap[value];
    }
    if (fieldName === 'rateCenterProvince') {
      updatedForm.rateCenter = '';
      updatedForm.npa = 0;
      updatedForm.nxx = 0;
      updatedForm.number = 0;
    }
    if (fieldName === 'rateCenter') {
      updatedForm.npa = 0;
      updatedForm.nxx = 0;
      updatedForm.number = 0;
    }
    updatedForm[fieldName] = updateValue;
    setCurrentForm(updatedForm);
    if (!isFormTouched) {
      setIsFormTouched(true);
    }
  };

  const onDelete = index => {
    forms.splice(index, 1);
    setCurrentIndex(forms.length);
    setForms([...forms]);
    if (!isFormTouched) {
      setIsFormTouched(true);
    }
  };

  const validateForm = () => {
    const result = {};
    Object.keys(formFieldMap).forEach(fieldKey => {
      if (formFieldMap[fieldKey].required && !currentForm[fieldKey]) {
        result[fieldKey] = fieldRequiredMessage;
      }
      if (!isRelatedFieldExisted(formFieldMap[fieldKey])) {
        delete result[fieldKey];
      }
    });
    return result;
  };

  const onEdit = index => {
    const newFormData = { ...forms[index] };
    setCurrentForm(newFormData);
    forms.splice(index, 1);
    setCurrentIndex(forms.length);
    setForms([...forms]);
  };

  const nextSeat = () => {
    const errorObject = validateForm();
    setFieldErrors(errorObject);
    if (Object.keys(errorObject).length === 0) {
      if (seatCount > currentIndex + 1) {
        if (currentForm[applyToX]) {
          const applyToXTimes = parseInt(currentForm[applyToX], 10);
          const filledArray = [];
          let newFormsData = forms;
          const device =
            orderData.devices &&
            orderData.devices.find(
              item => item.name === currentForm[deviceType]
            );
          const deviceMaxQty =
            getDeviceInitQuantity(currentForm[deviceType]) -
            getDeviceQuantity(currentForm[deviceType]);
          if (
            (typeof device !== 'undefined' ||
              currentForm[deviceType] === deviceBYOD) &&
            applyToXTimes > deviceMaxQty
          ) {
            setFieldErrors({
              applyToTheNextXSeatsOnThisOrder: stringInterpolation(
                errorTotalMessage,
                deviceMaxQty
              )
            });
          } else if (applyToXTimes + currentIndex + 1 > seatCount) {
            setFieldErrors({
              applyToTheNextXSeatsOnThisOrder: stringInterpolation(
                errorMessage,
                seatCount
              )
            });
          } else {
            if (currentIndex === 0) {
              currentForm[applyToX] = undefined;
              newFormsData = [];
            }
            setCurrentIndex(currentIndex + applyToXTimes);
            for (let i = 0; i < applyToXTimes; i += 1) {
              filledArray.push({ ...currentForm });
            }
            currentForm[deviceType] = undefined;
            setCurrentForm({ ...currentForm });
            setForms([...newFormsData, ...filledArray]);
          }
        } else {
          setCurrentIndex(currentIndex + 1);
          setForms([...forms, { ...currentForm }]);
          setCurrentForm({ ...formObjectKeys, creationDate: new Date() });
        }
      }
    }
  };

  const continueBtn = () => {
    const errorObject = validateForm();
    if (Object.keys(errorObject).length === 0) {
      if (seatCount === currentIndex + 1) {
        const updateFormsData = [...forms, currentForm].map(item => {
          return { ...item, [applyToX]: 0 };
        });
        submitOrder(updateFormsData);
      }
    } else {
      setFieldErrors(errorObject);
    }
  };

  const saveAndExit = () => {
    const errorObject = validateForm();
    if (Object.keys(errorObject).length === 0) {
      const updateFormsData = [...forms, currentForm];
      onSaveAndExit({ [key]: updateFormsData });
    } else {
      setFieldErrors(errorObject);
    }
  };

  const onPhoneNumberChange = (npa, nxx, number, type) => {
    let payload = { ...currentForm, npa, nxx, number };
    if (npa && nxx && number && number.length === 4) {
      payload = { ...payload, phoneNumber: `${npa}${nxx}${number}` };
    } else if (type === 'npa' && npa) {
      payload = { ...payload, phoneNumber: 0, nxx: 0, number: 0 };
    } else {
      payload = { ...payload, phoneNumber: 0 };
    }
    setCurrentForm({ ...payload });
  };

  const renderPhoneNumberField = () => {
    return (
      <div style={{ position: 'relative' }}>
        <div>
          <span className="frg-body-font-14px">
            {formFieldMap.phoneNumber.label}
          </span>
        </div>
        <div
          className="frg-select-container color-light"
          style={{ position: 'relative', width: '100px', height: '44px' }}
        >
          <select
            id="NPA"
            onChange={({ target: { value } }) =>
              onPhoneNumberChange(value, 0, 0, 'npa')
            }
            value={currentForm && currentForm.npa}
          >
            <option value="">{npaLabel}</option>
            {getNPAOptions(currentForm.rateCenterProvince).map(
              (value, index) => {
                const optionKey = `NPAOption${index}`;
                return (
                  !['name', 'newDidCount'].includes(value) && (
                    <option key={optionKey} value={value}>
                      {value}
                    </option>
                  )
                );
              }
            )}
          </select>
        </div>
        <span style={{ color: '#aaa', fontWeight: 900 }}>-</span>
        <div
          className="frg-select-container color-light"
          style={{ position: 'relative', width: '100px', height: '44px' }}
        >
          <select
            id="NXX"
            onChange={({ target: { value } }) =>
              onPhoneNumberChange(currentForm.npa, value, 0, 'nxx')
            }
            value={currentForm && currentForm.nxx}
          >
            <option value="">{nxxLabel}</option>
            {getNXXOptions(currentForm.rateCenterProvince).map(
              (value, index) => {
                const optionKey = `NXXOption${index}`;
                return (
                  <option key={optionKey} value={value}>
                    {value}
                  </option>
                );
              }
            )}
          </select>
        </div>
        <span style={{ color: '#aaa', fontWeight: 900 }}>-</span>
        <input
          type="text"
          maxLength={4}
          onChange={({ target: { value } }) => {
            // regex that accepts number or empty string
            const re = /^(\s*|\d+)$/;
            if (re.test(value)) {
              onPhoneNumberChange(currentForm.npa, currentForm.nxx, value);
            }
          }}
          className="frg-input-field color-light"
          style={{ width: '100px', display: 'inline-block' }}
          value={currentForm && currentForm.number}
        />
      </div>
    );
  };

  useEffect(() => {
    if (isc2fMigrationFlow) {
      handleFormField(fieldWillTheLocalPhoneNumberBePortedIn, yes);
    }
  }, []);

  const title = isOfficeSeat
    ? `${name} ${currentIndex + 1} of ${seatCount}`
    : name;

  const getButtons = () => (
    <SeatDetailsButtonWrapper>
      <div id="footer-wrapper">
        <BoxWrapper>
          <button
            type="button"
            onClick={prevStep}
            className="frg-button color-green"
          >
            {backButtonLabel}
          </button>
          {!isOrderFulfillmentTeam && (
            <button
              type="button"
              onClick={saveAndExit}
              style={{ marginLeft: '1rem' }}
              className={`frg-button ${
                isFormTouched ? 'color-purple' : 'state-disabled'
              }`}
            >
              {saveContinueLaterButtonLabel}
            </button>
          )}
          {isOfficeSeat && seatCount > 1 && currentIndex < +seatCount - 1 && (
            <button
              type="button"
              style={{ marginLeft: '1rem' }}
              className={`frg-button ${
                seatCount > currentIndex + 1 ? 'color-green' : 'state-disabled'
              }`}
              onClick={() => nextSeat()}
            >
              {nextSeatButtonLabel}
            </button>
          )}
          {((isOfficeSeat && currentIndex === +seatCount - 1) ||
            !isOfficeSeat) && (
            <button
              type="button"
              style={{ marginLeft: '1rem' }}
              className={`frg-button ${
                seatCount === currentIndex + 1
                  ? 'color-green'
                  : 'state-disabled'
              }`}
              onClick={() => continueBtn()}
            >
              {continueButtonLabel}
            </button>
          )}
        </BoxWrapper>
      </div>
    </SeatDetailsButtonWrapper>
  );

  return (
    <>
      {Object.keys(orderData).length === 0 || !rateCenterData ? (
        <ValidateLoader message={loading} />
      ) : (
        <>
          <GroupWrapper style={{ marginBottom: '1em' }}>
            <GroupTitle>{title}</GroupTitle>
            {formFields.map(({ fields }, index) => {
              if (
                isOfficeSeat &&
                currentIndex === +seatCount - 1 &&
                fields.name === 'applyToTheNextXSeatsOnThisOrder'
              )
                return null;
              const fieldKey = `SeatDetails${name}${index}${currentIndex}`;
              const fieldName = fields.name;
              const isDisabled =
                isc2fMigrationFlow &&
                [
                  fieldWillTheLocalPhoneNumberBePortedIn,
                  fieldProvider,
                  fieldWillTheTollFreeBeBortedIn
                ].includes(fieldName);

              const getOptions = options => {
                let result;
                switch (fieldName) {
                  case 'rateCenter':
                    result = getRateCenterOptions(
                      currentForm.rateCenterProvince
                    );
                    break;
                  case deviceType:
                    result = [...devices()];
                    break;
                  default:
                    result = options;
                }
                return result;
              };

              const fieldData = {
                ...formFieldMap[fieldName],
                readonly: isDisabled,
                disabled: isDisabled,
                options: getOptions(formFieldMap[fieldName].options),
                value: currentForm[fieldName],
                onChange: ({ target: { value } }) =>
                  handleFormField(fieldName, value)
              };

              const error = fieldErrors[fieldName];
              if (!isRelatedFieldExisted(fields)) return null;
              return (
                <Fragment key={fieldKey}>
                  {fieldName === 'phoneNumber' ? (
                    renderPhoneNumberField()
                  ) : (
                    <FormField data={fieldData} />
                  )}
                  {error && <Error text={error} />}
                </Fragment>
              );
            })}
          </GroupWrapper>
          <DimpleDivider />
          {forms.length > 0 && isOfficeSeat && (
            <SeatDetailsTable
              forms={forms}
              onDelete={onDelete}
              onEdit={onEdit}
            />
          )}
        </>
      )}
      {getButtons()}
    </>
  );
};

SeatDetailsForm.propTypes = {
  data: PropTypes.shape({
    key: PropTypes.string.isRequired,
    formFields: PropTypes.arrayOf(PropTypes.object),
    name: PropTypes.string.isRequired
  }).isRequired,
  orderData: PropTypes.objectOf(PropTypes.any).isRequired,
  updateStep: PropTypes.func.isRequired,
  step: PropTypes.number.isRequired,
  setOrderData: PropTypes.func.isRequired,
  onSaveAndExit: PropTypes.func.isRequired,
  isOrderFulfillmentTeam: PropTypes.bool.isRequired,
  prevStep: PropTypes.func.isRequired
};

export default SeatDetailsForm;
